import { FC, useEffect, useContext, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";


import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import { ToastContext } from "src/context/toastContext";
import { LoaderContext } from "src/context/loaderContext";
import { ToastTypes } from "src/types";

import FooterText from "src/components/footerText";
import CaseFilter from "src/components/manufacturer/statistics";
import { StatisticsSearchProps } from "src/components/manufacturer/statistics/types";
import {AuthContext} from "../../context/authContext";
import AuthMaunfacturerSubHeader from "src/components/manufacturer/authManufacturerSubHeader";
import CustomComponentForTransfer from "src/components/manufacturer/customComponentForTransfer";
import { GeographicalStatisticsObject, StatisticsApiResponse } from "../manufacturer/types";
import GeographicalStatistics from "src/components/manufacturer/geographicalStatistics";
import CustomPieChart from "src/components/manufacturer/customPieChart";
import CaseVolume from "src/components/manufacturer/caseVolumeLineChart";
import CustomComponentCasesByDrugsBarChart from "src/components/manufacturer/customComponentCasesByDrugsBarChart";
import CustomComponentDoctorsEnrollemnts from "src/components/manufacturer/customComponentDoctorsEnrollemnts";
import { areManFiltersUpdated, getDefaultManFilters } from "src/helpers/manufacturer";


const Territory: FC = (): JSX.Element => {
  const { showToast } = useContext(ToastContext);
  const { setLoader, hideLoader } = useContext(LoaderContext);
  const { userData } = useContext(AuthContext);
  const [searchParams, updateSearchParams] = useSearchParams();

  const [queryParams, changeQueryParams] = useState(getDefaultManFilters(searchParams, userData, "territories"));

  const { data: statisticsData, mutate: getStatsData } = 
    useFetch<StatisticsApiResponse>(APIEndpoints.MANUFACTURE_STATISTICS, queryParams);

  useEffect(() => {
    if (statisticsData) {
      hideLoader();
      if (statisticsData.code === 200) {
        showToast([statisticsData.message], ToastTypes.SUCCESS);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statisticsData]);



  const updateQueryParamsAndCallAPI = (
    updatedQueries: StatisticsSearchProps,
    isReset: boolean = false
  ): void => {
    if (isReset) {
      updateSearchParams({});
      changeQueryParams({
        start_date: format(
          new Date().setDate(new Date().getDate() - 30),
          "yyyy-MM-dd"
        ),
        end_date: format(
          new Date().setDate(new Date().getDate()),
          "yyyy-MM-dd"
        ),
        period: "",
        filter_type: "",
        role: userData ? userData.ref_type : "",
        type: "territories",
        user_id: userData && userData.ref_type !== "manufacturer" ? userData.id.toString() : "",
      });
    } else {
      updateSearchParams({
        ...queryParams,
        start_date: updatedQueries.start_date,
        end_date: updatedQueries.end_date,
        period: updatedQueries.period,
        filter_type: updatedQueries.filter_type
      });

      if (areManFiltersUpdated(queryParams, searchParams, userData, "territories"))
        changeQueryParams(updatedQueries);
    }

    setLoader();
    getStatsData({}, {}, updatedQueries);
  };


  return (
      <div className={"container-fluid overflow-y-auto"}>
        <div className="container">
          <AuthMaunfacturerSubHeader/>
          {statisticsData && (
            <CaseFilter
              defaultQueryParams={queryParams}
              updateQueryParams={updateQueryParamsAndCallAPI}
            />
          )}
           {statisticsData && (
            <CaseVolume StatisticsObjectType={statisticsData.data} buttonShows={false} />
          )}

          {statisticsData && (
            <CustomComponentCasesByDrugsBarChart StatisticsObjectType={statisticsData.data} />
          )}
          {statisticsData && (
            <div className="row">
              <div className="col-12 col-md-6">
                <CustomPieChart pieChartObject={statisticsData.data.top_payors} title="Top 10 Payors" />
              </div>
              <div className="col-12 col-md-6">
                <CustomPieChart 
                  pieChartObject={statisticsData.data.mos
                    .sort((item, item1) => {
                      const nameOne = item.name ?? ""
                      const nameTwo = item1.name ?? ""
                      return nameOne > nameTwo ? 1 : -1
                    })
                  } 
                  title="Cases By Submission Mode"
                />
              </div>
            </div>
          )}

          {(statisticsData &&
            <CustomComponentForTransfer StatisticsObjectType={statisticsData.data}/>
          )}

          {statisticsData && (
            <CustomComponentDoctorsEnrollemnts StatisticsObjectType={statisticsData.data} />
          )}

          {statisticsData?.data && (
            <GeographicalStatistics
              GeographicalStatisticsListType={
                statisticsData?.data
                  .geographical_stats as unknown as GeographicalStatisticsObject[]
              }
            />
          )}
          <FooterText />
        </div>
      </div>
  );
};

export default Territory;
